import React from 'react';
import { Box, Stack, Skeleton, useTheme, useMediaQuery } from '@mui/material';

const ApprovalCardSkeleton: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isLargeTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    return (
        <Box
            sx={{
                backgroundColor: '#FCFCFC',
                borderRadius: '16px',
                px: { xs: 2, sm: 2.5, md: 3, lg: 4 },
                py: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                border: '1px solid #F2F0F0',
                fontFamily: 'Poppins, sans-serif',
                mb: { xs: 2, sm: 2.5, md: 3 },
            }}
        >
            {/* Header */}
            <Stack 
                direction={isMobile ? 'column' : 'row'} 
                alignItems={isMobile ? 'stretch' : 'flex-start'} 
                justifyContent='space-between'
                spacing={isMobile ? 2 : 0}
            >
                {/* Status + Incident */}
                <Stack spacing={1} minWidth={isMobile ? '100%' : (isTablet ? 140 : 160)} sx={{ flexShrink: 0 }}>
                    <Skeleton
                        variant='rectangular'
                        width={100}
                        height={isTablet ? 28 : 32}
                        sx={{
                            borderRadius: '24px',
                            backgroundColor: '#f0f0f0',
                        }}
                    />
                    {!isMobile && (
                        <>
                            <Skeleton
                                variant='text'
                                width={110}
                                height={16}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                            <Skeleton
                                variant='text'
                                width={90}
                                height={16}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                        </>
                    )}
                </Stack>

                <Stack 
                    direction={isMobile ? 'column' : 'row'} 
                    spacing={isMobile ? 2 : (isTablet ? 3 : (isLargeTablet ? 5 : 8))} 
                    sx={{ 
                        mr: isMobile ? 0 : (isTablet ? 8 : (isLargeTablet ? 15 : 25)),
                        flex: 1,
                        minWidth: 0 // Allow content to shrink
                    }}
                >
                    {/* Left Column */}
                    <Stack spacing={isMobile ? 1.5 : (isTablet ? 1.5 : 2)} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack spacing={0.5}>
                            <Skeleton
                                variant='text'
                                width={60}
                                height={16}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                            <Skeleton
                                variant='text'
                                width={80}
                                height={20}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                        </Stack>
                        <Stack spacing={0.5}>
                            <Skeleton
                                variant='text'
                                width={70}
                                height={16}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                            <Skeleton
                                variant='text'
                                width={90}
                                height={20}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                        </Stack>
                    </Stack>

                    {/* Right Column */}
                    <Stack spacing={isMobile ? 1.5 : (isTablet ? 1.5 : 2)} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack spacing={0.5}>
                            <Skeleton
                                variant='text'
                                width={100}
                                height={16}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                            <Skeleton
                                variant='text'
                                width={70}
                                height={20}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                        </Stack>
                        <Stack spacing={0.5}>
                            <Skeleton
                                variant='text'
                                width={90}
                                height={16}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                            <Skeleton
                                variant='text'
                                width={80}
                                height={20}
                                sx={{ backgroundColor: '#f0f0f0' }}
                            />
                        </Stack>
                    </Stack>
                </Stack>

                {/* Buttons + Toggle */}
                {!isMobile && (
                    <Stack direction='row' spacing={isTablet ? 0.5 : 1} alignItems='center' sx={{ flexShrink: 0 }}>
                        <Skeleton
                            variant='rectangular'
                            width={isTablet ? 65 : 84}
                            height={isTablet ? 40 : 48}
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: '#f0f0f0',
                            }}
                        />
                        <Skeleton
                            variant='rectangular'
                            width={isTablet ? 65 : 84}
                            height={isTablet ? 40 : 48}
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: '#f0f0f0',
                            }}
                        />
                        <Skeleton
                            variant='circular'
                            width={isTablet ? 32 : 40}
                            height={isTablet ? 32 : 40}
                            sx={{ backgroundColor: '#f0f0f0' }}
                        />
                    </Stack>
                )}

                {/* Mobile Action Buttons */}
                {isMobile && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Skeleton
                            variant='rectangular'
                            width={65}
                            height={36}
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: '#f0f0f0',
                            }}
                        />
                        <Skeleton
                            variant='rectangular'
                            width={65}
                            height={36}
                            sx={{
                                borderRadius: '12px',
                                backgroundColor: '#f0f0f0',
                            }}
                        />
                        <Skeleton
                            variant='circular'
                            width={32}
                            height={32}
                            sx={{ backgroundColor: '#f0f0f0' }}
                        />
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};

export default ApprovalCardSkeleton;
